import { useState } from 'react'
import { AppRoot } from '../components/AppRoot'
import { Button, TextField } from '../ui'
import { sendOtp, verifyOtp, signInWithOAuth } from '../lib/auth'

export interface SignInScreenProps {
  onSignedIn?: () => void
  /** Optional context shown above the form — e.g. when embedded by another screen. */
  contextMessage?: string
}

type Step = 'email' | 'code'

const PROVIDER_LABELS: Record<'google' | 'azure', string> = {
  google: 'Continue with Google',
  azure: 'Continue with Microsoft',
}

/**
 * Splits a comma-separated `VITE_OAUTH_PROVIDERS` value into the recognized
 * provider ids `signInWithOAuth` accepts, silently dropping anything else.
 * This is a build-time config flag Oscar sets himself (decision 3: OAuth
 * wired but dark this phase), not user input, so a typo just means the
 * button doesn't appear — no validation error is warranted.
 *
 * Exported so it can be unit-tested directly, and called at render time
 * (never frozen as a module-level constant) so a test can `vi.stubEnv`
 * before rendering and have the component pick it up on that render.
 */
export function parseOAuthProviders(raw: string | undefined): Array<'google' | 'azure'> {
  if (!raw) return []
  return raw
    .split(',')
    .map((piece) => piece.trim())
    .filter((piece): piece is 'google' | 'azure' => piece === 'google' || piece === 'azure')
}

/**
 * Two steps, held in local `useState` — email, then the 6-digit OTP code
 * sent to it. A single `TextField` carries the code (not six digit boxes;
 * see this task's brief for why), with `autoComplete="one-time-code"` so a
 * phone can offer one-tap autofill.
 *
 * Callback-prop navigation, matching `Onboarding`'s `onComplete` and
 * `AppShell`'s `onProfileChange`: this screen calls `onSignedIn?.()` once
 * `verifyOtp` succeeds and does nothing else. It never reads the returned
 * session — `SessionProvider`'s own `onAuthStateChange` subscription picks
 * up the new session on its own.
 *
 * A failed `sendOtp` keeps the user on the email step; a failed `verifyOtp`
 * keeps them on the code step — the email was already accepted, only the
 * code was wrong or expired, so bouncing back to re-enter the email would
 * be a false demotion.
 *
 * `contextMessage`, when given, renders as a hint line above the form —
 * for a caller that embeds this screen (`JoinScreen`, Task 14) and needs to
 * explain why sign-in is showing up. This screen always wraps itself in
 * exactly one `AppRoot`, so an embedding caller renders `<SignInScreen
 * contextMessage="…" />` directly rather than wrapping it in a second one.
 */
export function SignInScreen({ onSignedIn, contextMessage }: SignInScreenProps) {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sending, setSending] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [oauthError, setOauthError] = useState<string | null>(null)

  const providers = parseOAuthProviders(import.meta.env.VITE_OAUTH_PROVIDERS)

  async function handleSendCode() {
    setSending(true)
    setEmailError(null)
    try {
      const { error } = await sendOtp(email.trim())
      if (error) {
        setEmailError(error.message)
        return
      }
      setStep('code')
    } finally {
      setSending(false)
    }
  }

  async function handleVerify() {
    setSending(true)
    setCodeError(null)
    try {
      const { error } = await verifyOtp(email.trim(), code)
      if (error) {
        setCodeError(error.message)
        return
      }
      onSignedIn?.()
    } finally {
      setSending(false)
    }
  }

  async function handleOAuth(provider: 'google' | 'azure') {
    setOauthError(null)
    const { error } = await signInWithOAuth(provider)
    if (error) {
      setOauthError(error.message)
    }
  }

  function handleUseDifferentEmail() {
    setStep('email')
    setCode('')
    setCodeError(null)
  }

  return (
    <AppRoot>
      <div className="sl-stack">
        {contextMessage ? <p className="sl-hint">{contextMessage}</p> : null}
        {step === 'email' ? (
          <>
            <div>
              <h1 className="sl-heading">Sign in</h1>
              <p className="sl-hint">We'll email you a 6-digit code — no password needed.</p>
            </div>
            <TextField
              type="email"
              label="Email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
            />
            {emailError ? (
              <p className="sl-hint" role="alert">
                {emailError}
              </p>
            ) : null}
            <Button variant="primary" block disabled={sending} onClick={handleSendCode}>
              Send code
            </Button>
          </>
        ) : (
          <>
            <div>
              <h1 className="sl-heading">Enter your code</h1>
              <p className="sl-hint">Enter the code we sent to {email}.</p>
            </div>
            <TextField
              label="Code"
              value={code}
              onChange={setCode}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]*"
            />
            {codeError ? (
              <p className="sl-hint" role="alert">
                {codeError}
              </p>
            ) : null}
            <Button variant="primary" block disabled={sending} onClick={handleVerify}>
              Verify
            </Button>
            <Button variant="ghost" block onClick={handleUseDifferentEmail}>
              Use a different email
            </Button>
          </>
        )}
        {providers.length > 0 ? (
          <div className="sl-stack">
            {providers.map((provider) => (
              <Button
                key={provider}
                variant="secondary"
                block
                onClick={() => handleOAuth(provider)}
              >
                {PROVIDER_LABELS[provider]}
              </Button>
            ))}
            {oauthError ? (
              <p className="sl-hint" role="alert">
                {oauthError}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </AppRoot>
  )
}
